import { useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, KeyRound, Link2, Loader2, ShieldQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { CredentialHealth } from "@/components/credentials/CredentialShell";
import { validationFeedback } from "@/lib/hooks/useCredentialSave";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { cn } from "@/lib/utils";

interface ShopeeCredentialSectionProps {
  readonly credential: AffiliateCredentialPublic | undefined;
  readonly loadingCredentials: boolean;
  readonly sectionId?: string | undefined;
}

export function ShopeeCredentialSection({
  credential,
  loadingCredentials,
  sectionId,
}: ShopeeCredentialSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const [partnerId, setPartnerId] = useState("");
  const [partnerKey, setPartnerKey] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const hasPartnerSecret = Boolean(credential?.config_public?.hasPartnerSecret);
  const authorizationStatus =
    typeof credential?.config_public?.authorizationStatus === "string"
      ? credential.config_public.authorizationStatus
      : "pending";
  const environment =
    typeof credential?.config_public?.environment === "string"
      ? credential.config_public.environment
      : "production";
  const isConfigured = Boolean(credential?.public_id && hasPartnerSecret);

  const handleSaveCredential = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const normalizedPartnerId = partnerId.trim();
    const normalizedPartnerKey = partnerKey.trim();
    setStatus(null);
    setError(null);

    if (!normalizedPartnerId || !normalizedPartnerKey) {
      setError("Informe Partner ID e Partner Key.");
      return;
    }

    setSaving(true);
    const { error: rpcError } = await supabase.rpc("upsert_shopee_affiliate_credential", {
      p_partner_id: normalizedPartnerId,
      p_partner_key: normalizedPartnerKey,
      p_environment: "production",
      p_authorization_status: "needs_shop_authorization",
    });
    setSaving(false);

    if (rpcError) {
      setError(shopeeRpcErrorLabel(rpcError.message));
      return;
    }

    setPartnerId("");
    setPartnerKey("");
    const feedback = await validationFeedback("shopee");
    setStatus(feedback.status);
    setError(feedback.error);
    await queryClient.invalidateQueries({ queryKey: ["affiliate-credentials"] });
  };

  return (
    <section
      id={sectionId}
      className="overflow-hidden rounded-xl border border-border bg-card"
      aria-labelledby="section-shopee"
    >
      <div className="flex items-center justify-between gap-2.5 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
        <h2
          id="section-shopee"
          className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
        >
          Shopee
        </h2>
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold",
            isConfigured
              ? "border-primary/25 bg-primary/10 text-primary"
              : "border-warning/25 bg-warning/10 text-warning",
          )}
        >
          {isConfigured ? "configurado" : "em implantação"}
        </span>
      </div>

      <div className="space-y-5 p-5">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">Conector Shopee Open Platform</p>
          <p className="text-xs text-muted-foreground">
            Esta etapa está em preparo para API oficial. Hoje a Shopee exige aprovação de parceiro e OAuth de
            autorização da loja (auth_partner) para gerar links rastreáveis.
          </p>
        </div>

        <div className="space-y-2 rounded-lg border border-border bg-[hsl(var(--surface)/0.75)] p-3">
          <div className="flex items-center gap-2">
            <Link2 size={15} className="text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">Config de parceiro</p>
          </div>
          {!loadingCredentials && credential ? (
            <>
              <p className="font-mono text-xs text-foreground break-all">
                Partner ID atual: {credential.public_id ?? "Não informado"}
              </p>
              <p className="font-mono text-xs text-foreground">
                Ambiente: {environment}
              </p>
              <p className="font-mono text-xs text-foreground">
                Autorização: {authorizationStatus}
              </p>
              <p className="text-xs text-muted-foreground">
                Chave secreta no Vault: {hasPartnerSecret ? "sim" : "não"}
              </p>
            </>
          ) : null}
          {loadingCredentials && <p className="text-xs text-muted-foreground">Carregando credencial.</p>}
        </div>

        <CredentialHealth credential={credential} />

        <form onSubmit={handleSaveCredential} className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="shopee-partner-id" variant="field">Partner ID</Label>
            <Input
              id="shopee-partner-id"
              value={partnerId}
              onChange={(event) => setPartnerId(event.target.value)}
              placeholder="12345678"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="shopee-partner-key" variant="field">Partner Key</Label>
            <Input
              id="shopee-partner-key"
              type="password"
              value={partnerKey}
              onChange={(event) => setPartnerKey(event.target.value)}
              placeholder="seu partner key"
              autoComplete="off"
            />
          </div>

          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" variant="gold" disabled={saving}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
              Salvar Shopee
            </Button>
          </div>
        </form>

        {status && (
          <InlineNotice tone="neutral" icon={<CheckCircle2 size={15} />}>
            {status}
          </InlineNotice>
        )}

        {error && (
          <InlineNotice tone="warning" icon={<AlertCircle size={15} />}>
            {error}
          </InlineNotice>
        )}

        <InlineNotice tone="warning" icon={<ShieldQuestion size={15} />}>
          Esta integração não gera link automaticamente sem autorização de loja. O bot permanece no modo bloqueado
          até a etapa de OAuth estar conectada.
        </InlineNotice>
      </div>
    </section>
  );
}

function shopeeRpcErrorLabel(message: string): string {
  if (message.includes("invalid_shopee_partner_id")) return "Partner ID inválido.";
  if (message.includes("invalid_shopee_partner_key")) return "Partner Key inválida.";
  if (message.includes("invalid_shopee_environment")) return "Ambiente inválido.";
  if (message.includes("tenant_not_found")) return "Tenant ativo não encontrado.";
  return "Não foi possível salvar a credencial Shopee agora.";
}

function InlineNotice({
  children,
  icon,
  tone,
}: {
  children: ReactNode;
  icon: ReactNode;
  tone: "neutral" | "warning";
}): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
        tone === "warning"
          ? "border-warning/25 bg-warning/10 text-warning"
          : "border-border bg-[hsl(var(--surface)/0.75)] text-muted-foreground",
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}
